import React from "react";
import { useTranslation } from "react-i18next";

function EventLocation() {
  const { t } = useTranslation();

  return (
    <section className="pt-12 pb-12 bg-white dark:bg-slate-800 transition-colors relative z-10" id="location">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("eventLocation.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("eventLocation.description")}
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-8 rounded-xl overflow-hidden shadow-xl">
          <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
            <img 
              src={process.env.PUBLIC_URL + "/images/arena.jpg"} 
              alt={t("eventLocation.imageAlt")}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h3 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3 mb-2">
                <img
                  src={process.env.PUBLIC_URL + "/images/sponsors/sparkasse.png"}
                  alt="Sparkasse Logo"
                  className="h-8 md:h-10 w-auto filter drop-shadow-lg"
                />
                <span>{t("eventLocation.arenaName")}</span>
              </h3>
            </div>
          </div>
        </div>

        {/* Info and Map Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Info Card */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 lg:p-8">
            <div className="space-y-5">
              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-map-marker-alt text-primary dark:text-primary-light text-lg"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    {t("eventLocation.address")?.includes(":") ? t("eventLocation.address").split(":")[0] : "Adresse"}
                  </h4>
                  <p className="text-slate-900 dark:text-slate-100 text-base leading-relaxed">
                    {t("eventLocation.address")?.includes(":") ? t("eventLocation.address").split(":")[1].trim() : t("eventLocation.address")}
                  </p>
                </div>
              </div>

              {/* Public Transport */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-bus text-primary dark:text-primary-light text-lg"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    {t("eventLocation.publicTransport")?.includes(":") ? t("eventLocation.publicTransport").split(":")[0] : "Öffentliche Verkehrsmittel"}
                  </h4>
                  <p className="text-slate-900 dark:text-slate-100 text-base leading-relaxed">
                    {t("eventLocation.publicTransport")?.includes(":") ? t("eventLocation.publicTransport").split(":")[1].trim() : t("eventLocation.publicTransport")}
                  </p>
                </div>
              </div>

              {/* Parking */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-parking text-primary dark:text-primary-light text-lg"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Parken
                  </h4>
                  <p className="text-slate-900 dark:text-slate-100 text-base leading-relaxed">
                    {t("eventLocation.parking")}
                  </p>
                </div>
              </div>

              {/* Capacity */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-users text-primary dark:text-primary-light text-lg"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Kapazität
                  </h4>
                  <p className="text-slate-900 dark:text-slate-100 text-base leading-relaxed">
                    {t("eventLocation.capacity")}
                  </p>
                </div>
              </div>

              {/* Accessibility */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-wheelchair text-primary dark:text-primary-light text-lg"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Barrierefreiheit
                  </h4>
                  <p className="text-slate-900 dark:text-slate-100 text-base leading-relaxed">
                    {t("eventLocation.accessibility")}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
                {/* Route Button */}
                <a
                  href="https://maps.app.goo.gl/xsVNfuTcjFbTxpFS9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 shadow-sm hover:shadow-md w-full justify-center"
                >
                  <i className="fas fa-directions"></i>
                  {t("eventLocation.planRoute")}
                </a>
              </div>
            </div>
          </div>

          {/* Map Card */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="w-full h-full min-h-[400px] lg:min-h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2033.8983804711252!2d13.584893816301923!3d52.682977852244385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a9b3ef32881057%3A0xaaa4953f3df28761!2sS-Arena%20Bernau!5e0!3m2!1sde!2sde!4v1730122476432!5m2!1sde!2sde"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen
                loading="lazy"
                title={t("eventLocation.mapTitle")}
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EventLocation;
